<template>
	<Page :class="['summer-sale-offer', `type-${paywallType}`]">
		<template #content>
			<SafeAreaTop theme="dark-invert" @close="close" />
			<div class="image-container"></div>
			<div class="container">
				<div class="title" v-html="t('title')"></div>
				<div class="points">
					<div class="points-item" v-for="item in points" :key="item.id">
						<svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
							<circle cx="10.0713" cy="10.9238" r="10" class="point-icon-circle" />
							<path
								d="M7.09473 10.9345L9.50448 13.3443L13.338 9.51079"
								stroke-width="2"
								stroke-linecap="round"
								class="point-icon-check"
							/>
						</svg>
						<div class="points-text">{{ item.text }}</div>
					</div>
				</div>

				<ProductsContainer
					:is-android="isAndroid"
					:show-terms="false"
					:products-theme="paywallType"
					:top-product="topProduct"
					:bottom-product="bottomProduct"
					@change="handleChangeProduct"
				/>
			</div>

			<Prompt
				:isOpen="showPaywallCancelPrompt"
				:title="t('cancel_popup.title')"
				:text="t('cancel_popup.body', { store: !isAndroid ? 'iTunes Store' : 'Play Store' })"
				:action="t('cancel_popup.action')"
				@onClose="onClosePaywallCancelPrompt"
				@onAction="onActionPaywallCancelPrompt"
			/>

			<Alert
				:isOpen="showRestoreSuccessAlert"
				:title="t('restore_popup.success.title')"
				:text="t('restore_popup.success.body')"
				@onClose="onCloseRestoreSuccessAlert"
			/>
			<Alert
				:isOpen="showRestoreErrorAlert"
				:title="t('restore_popup.error.title')"
				:text="t('restore_popup.error.body')"
				@onClose="onCloseRestoreErrorAlert"
			/>
		</template>
		<template v-slot:footer>
			<div class="container">
				<p-button @click.prevent="handleSubmit" label="" :loading="isLoading" cls="btn-summer-sale btn-block">
					{{ t('continue') }}
				</p-button>
				<PaywallLinksBlock class="mb-sm" @restore="handleRestore" />
				<div class="terms" v-if="activeProduct && activeProduct.terms">{{ activeProduct.terms }}</div>
			</div>
		</template>
	</Page>
</template>

<script setup>
import Page from '@/components/Page'
import SafeAreaTop from '@/components/SafeAreaTop'
import { computed, ref } from '@vue/reactivity'
import PButton from '@/components/Button'
import { useI18n } from 'vue-i18n'
import useApi from '@/composition/api'
import { inject } from '@vue/runtime-core'
import { useStore } from 'vuex'
import useConfig from '@/composition/config'
import { useRouter } from 'vue-router'
import Prompt from '@/components/Prompt'
import PaywallLinksBlock from '@/components/PaywallLinksBlock'
import Alert from '@/components/Alert'
import ProductsContainer from '@/components/products/ProductsContainer.vue'

const { t } = useI18n({
	messages: {
		en: {
			title: 'Reach your health goal <br /> this summer'
		}
	}
})
const api = useApi()
const amplitude = inject('amplitude')
const store = useStore()
const config = useConfig()

const activeProduct = ref(null)
const afterSubscription = computed(() => config.get({ key: 'after_subscription', default_value: null }))
const showPaywallCancelPrompt = ref(false)
const next = computed(() => config.get({ key: 'postPaid.type', default_value: null }))
const router = useRouter()
const isAndroid = api.getPlatform() == 'android'
const isLoading = computed(() => store.state.isLoading)
const showRestoreSuccessAlert = ref(false)
const showRestoreErrorAlert = ref(false)
const purchaseOnChangeProduct = computed(() => config.get({ key: 'onChangeProductPurchase', default_value: false }))

const topProduct = ref({
	id: 'simple_high_full_price_subscription_12_months_ss',
	text: t('product.yearly_title'),
	subtitle: t('product.price_per_month'),
	terms: t('product.annyally_terms'),
	badge_only_digits: true,
	badge_digits: t('product.badge_digits', { count: 75 })
})

const bottomProduct = ref({
	id: 'simple_original_subscription_1_month',
	badge: null,
	text: t('product.monthly_title'),
	subtitle: t('product.price_per_month'),
	terms: t('product.monthly_terms')
})

// eslint-disable-next-line no-undef
const props = defineProps({
	paywallTheme: {
		type: String,
		default: 'ss_orange'
	}
})

const paywallType = computed(() => props.paywallTheme)

const points = computed(() => {
	return [
		{
			id: 1,
			text: 'Guidance from nutrition experts'
		},
		{
			id: 2,
			text: 'Premium food scanner'
		},
		{
			id: 3,
			text: 'Inspirational content'
		}
	]
})

function handleChangeProduct(product) {
	if (product.id == activeProduct.value?.id && isAndroid) {
		handleSubmit()
	} else {
		activeProduct.value = product
		if (purchaseOnChangeProduct.value) {
			handleSubmit()
		}
	}
}

function handleSubmit() {
	purchase(activeProduct.value)
}

function purchase(product) {
	api.purchase({
		productId: product.id,
		onFinish: () => {
			amplitude.logEvent('Paywall - Done', {
				Layout: 'Summer Sale',
				From: store.getters['getSource'],
				Step: 'Paywall',
				ProductId: product.id,
				Status: 'Subscribe'
			})
			close('Subscribe', afterSubscription.value || next.value)
		},
		onError: () => {
			console.log('error')
		},
		onCancel: () => {
			showPaywallCancelPrompt.value = true
		},
		closeOnSuccess: false
	})
}

function close(Status = 'Close', nextRoute = null) {
	amplitude.logEvent('Paywall - Close', {
		Layout: 'Summer Sale',
		From: store.getters['getSource'],
		Step: 'Paywall',
		Status
	})

	if (isAndroid || (!nextRoute && !next.value)) {
		api.close()
	} else {
		router.push({
			name: nextRoute || next.value,
			query: {
				Status
			}
		})
	}
}

function onClosePaywallCancelPrompt() {
	showPaywallCancelPrompt.value = false
}

function onActionPaywallCancelPrompt() {
	showPaywallCancelPrompt.value = false
	purchase(activeProduct.value)
}

function handleRestore() {
	api.restore({
		closeOnSuccess: false,
		onFinish: () => {
			showRestoreSuccessAlert.value = true
		},
		onFail: () => {
			showRestoreErrorAlert.value = true
		}
	})
}

function onCloseRestoreSuccessAlert() {
	showRestoreErrorAlert.value = false
	close('Restore')
}

function onCloseRestoreErrorAlert() {
	showRestoreErrorAlert.value = false
}
</script>

<style scoped lang="scss">
.summer-sale-offer {
	&.type-ss_orange {
		.image-container {
			background-image: url(~@/assets/paywall/summer-sale/ss-o-1.png);
		}

		.point-icon-circle {
			fill: #fff6d2;
		}

		.point-icon-check {
			stroke: #ef7e21;
		}
	}

	&.type-ss_ice {
		.image-container {
			background-image: url(~@/assets/paywall/summer-sale/ss-o-2.png);
		}

		.point-icon-circle {
			fill: rgba(255, 175, 112, 0.2);
		}

		.point-icon-check {
			stroke: #ed6d08;
		}
	}

	&.type-ss_swimsuit {
		.image-container {
			background-image: url(~@/assets/paywall/summer-sale/ss-o-3.png);
		}

		.point-icon-circle {
			fill: rgba(255, 175, 112, 0.2);
		}

		.point-icon-check {
			stroke: #ed6d08;
		}
	}
}

.image-container {
	width: 100%;
	height: 254px;
	background-size: 100% auto;
	background-repeat: no-repeat;
	background-position: 0 100%;
}

img {
	width: 100%;
	max-width: 100%;
	max-height: none;
	height: auto;
}

.title {
	font-weight: bold;
	text-align: center;
	font-size: 1.9rem;
	line-height: 1.2;
	padding-top: 2.6vh;
}

.points {
	width: 273px;
	margin: 0 auto;
	padding-top: 8px;
	font-size: 1.2rem;
}

.points-item {
	display: flex;

	&:not(:last-child) {
		margin-bottom: 6px;
	}

	svg {
		margin-right: 8px;
		width: 22px;
	}
}

:deep(.links) {
	margin-top: 2rem;
}

:deep(.products-container) {
	margin-top: 24px;
}

.terms {
	font-size: 0.9rem;
	width: 80%;
	margin: 0 auto;
}
</style>
